import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {IconChevronDoubleRight, LogoHp, Scrollbar, SideMenu as VeneerSideMenu} from '@veneer/core';

import {transitionSpeed} from '../../config/vars';
import {useWolfTheme} from '../../contexts';
import {useMediaQuery} from '../../hooks';
import {Footer} from '../Footer';
import {WFCButton} from '../WFCButton';

import useStyle from './styles';
import {NavProps, SideMenuToggleCallback} from './types';

// If either of these change the css for the side menu and app header should be checked closely.
export const NAV_OPEN_WIDTH = '260px';
export const NAV_CLOSED_WIDTH = '56px';

const SideMenu = ({
    nav: Nav,
    defaultPinned = false,
    controlledState = false,
    onToggle,
}: {
    controlledState?: boolean;
    nav: React.JSXElementConstructor<NavProps>;
    onToggle?: SideMenuToggleCallback;
    defaultPinned?: boolean;
}) => {
    const sideMenuRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLDivElement>(null);
    const [sideMenuOpen, setSideMenuOpen] = useState(defaultPinned);
    const [sideMenuPreview, setSideMenuPreview] = useState(false);

    const {t} = useTranslation(['SideMenu']);

    useLayoutEffect(() => {
        // istanbul ignore else
        if (sideMenuRef.current) {
            const nav = sideMenuRef.current.querySelector('nav');
            const ul = sideMenuRef.current.querySelector('ul');

            // istanbul ignore else
            if (nav && ul) {
                nav.style.display = 'flex';
                nav.style.transition = transitionSpeed;
                ul.style.height = '100%';
            }
        }
    }, []);

    useEffect(() => {
        const handleMouseOut = (): void => {
            if (!sideMenuOpen) {
                setSideMenuPreview(false);
            }
        };
        const handleMouseIn = (): void => {
            if (!sideMenuOpen) {
                setSideMenuPreview(true);
            }
        };
        // istanbul ignore else
        if (navRef.current) {
            const menu = navRef.current;
            menu.addEventListener('mouseenter', handleMouseIn);
            menu.addEventListener('mouseleave', handleMouseOut);

            return () => {
                menu.removeEventListener('mouseenter', handleMouseIn);
                menu.removeEventListener('mouseleave', handleMouseOut);
            };
        }
    }, [sideMenuOpen, sideMenuPreview]);

    const theme = useWolfTheme();
    const isLgScreen = useMediaQuery('lg');

    let open = sideMenuOpen;
    let preview = sideMenuPreview;

    if (!isLgScreen || controlledState) {
        open = controlledState;
        preview = false;
    }

    const style = useStyle(open, preview, controlledState);

    const handleToggle = useCallback(() => {
        setSideMenuOpen(curr => {
            onToggle?.(!curr);
            return !curr;
        });
    }, [onToggle]);

    const toggleLabel = t('SideMenu:sideMenuToggle', 'Toggle Side Menu');

    return (
        <div className={style.containerStyle}>
            <div
                className={style.menuContainer}
                data-testid="side-menu-wrapper"
            >
                <div className={style.menuContainerStyle}>
                    <VeneerSideMenu
                        data-testid="veneer-side-menu"
                        ref={sideMenuRef}
                        position="static"
                        size="small"
                        showIcons
                        expanded={open || preview || !isLgScreen}
                        hideAppHeader
                        i18n={{toggle: toggleLabel}}
                        customStyle={{borderRight: `1px solid ${theme.palette.stroke.default}`}}
                    >
                        <div className={style.menuInnerStyle}>
                            <Scrollbar
                                data-testid="side-menu--navigation-wrapper"
                                customStyle={style.scrollBar}
                                ref={navRef}
                            >
                                <Nav
                                    pinned={open}
                                    preview={preview}
                                />
                            </Scrollbar>

                            <div className={style.logo}>
                                <LogoHp appearance="singlecolor" />
                            </div>

                            <div className={style.toggleWrapper}>
                                <div
                                    className={style.toggle}
                                    data-testid="toggle"
                                >
                                    <WFCButton
                                        aria-label={toggleLabel}
                                        onClick={handleToggle}
                                        leadingIcon={<IconChevronDoubleRight />}
                                    />
                                </div>
                            </div>
                            <div className={style.footerStyles}>
                                <Footer />
                            </div>
                        </div>
                    </VeneerSideMenu>
                </div>
            </div>
        </div>
    );
};

export default SideMenu;
